import React, {useEffect, useState} from 'react';
import { Statuses } from 'interfaces/ITask.interface';
import Button from '@salesforce/design-system-react/components/button';
import Icon from '@salesforce/design-system-react/components/icon';
import CQBadge from '@salesforce/design-system-react/components/badge';
import { useTranslation } from 'react-i18next';
import CQSpinner from 'components/cq-spinner';
import Checkbox from '@salesforce/design-system-react/components/checkbox';
import { IBaseTask } from 'interfaces/IUpgradeSchema.interface';
import { CQSubTask } from '../cq-sub-task';
import CQMigrationTaskV2 from '../cq-migration-task-v2';

const Badge :any = CQBadge;

export interface CQTaskProperties {
    task: IBaseTask;
    readonly ?: boolean;
    onSelected ?: (task : IBaseTask, isSelected : boolean) => void;
    runIndividually ?: boolean;
    variables: any;
    defaultChecked: boolean;
}

export default function CQTask(props: CQTaskProperties) {
    const {t, i18n} = useTranslation();
    const [taskStatus, setTaskStatus] = useState(Statuses.Unknown);
    const [isSelected, setIsSelected] = useState(true);
    const task = props.task;
    const isReadonly = props.readonly || false;
    const runIndividually = props.runIndividually || false;
    const defaultChecked = props.defaultChecked;
    const [description, setDescription] = useState<JSX.Element | null>(null);

    useEffect(() => {
        defaultChecked === false ? setIsSelected(false) : setIsSelected(true);
        if(props.onSelected && defaultChecked === false){
            props.onSelected(task, defaultChecked);
            task.defaultChecked = undefined;
        }
        setTaskStatus(task.taskStatus|| Statuses.Unknown);
    },[props.task, props.task.taskStatus]);

    function getTaskIcon(task: IBaseTask) {
        let iconName = '';
        let colorVariant : any = 'default';
        switch(taskStatus) {
            case Statuses.Completed:
                iconName = "check";
                colorVariant = "success";
                break;
            case Statuses.Todo:
                iconName="list";
                colorVariant="default";
                break;
            case Statuses.Pending:
                return (props.task.isManual ? 
                <Icon
                    assistiveText={{ label: 'Pause' }}
                    category="utility"
                    name="pause"
                    size="medium"
                /> : <CQSpinner />);
            case Statuses.Unknown:
                iconName = 'routing_offline';
                colorVariant = 'warning';
                break;
            case Statuses.Failed:
                iconName = "warning";
                colorVariant = "error";
                break;
            default:
                return <></>
        }

        return <Icon
        assistiveText={{ label: t('common_is_complete') }}
        category="utility"
        name={iconName}
        size="x-small"
        colorVariant={colorVariant}
    />;
    }

    function getManualTaskButtons(task: IBaseTask) {
        if(taskStatus === Statuses.Todo) {
            return <Button
            variant="neutral"
            label={t("task_mark_as_complete")}
            iconCategory="utility"
            iconPosition="left"
            iconName="check"
            onClick={() => {
                console.log('Mark as complete clicked');
            }}
        />
        }
        return <></>;
    }

    function getTaskButton(task: IBaseTask) {
        if(task.isManual) {
            return getManualTaskButtons(task);
        }
        let iconType = 'play'
        switch(taskStatus) {
            case Statuses.Failed:
            case Statuses.Completed:
                iconType = 'refresh';
                break;

            case Statuses.Pending:
            case Statuses.Unknown:
                return <></>;

        }

        return <Button
            assistiveText={{ icon: 'Start task' }}
            iconCategory="utility"
            iconName={iconType}
            iconSize="medium"
            iconVariant="border"
            onClick={() => {
                console.log('Start task Clicked');
            }}
            variant="icon"
        />
    }

    function getErrorMessage(task:any){
        let taskMessage = JSON.parse(task?.errorMsg);
        return taskMessage?.message?taskMessage.message : task.errorMsg;
    }

    function getDescriptionKey(name: string) {
        return 'task_' + name + '_description';
    }

    useEffect(() => {
        if(task) {
            setTaskStatus(task.taskStatus|| Statuses.Unknown);
        }
    }, [task, task.taskStatus, task.progress]);

    useEffect(() => {
        if (task.inputs.description) {
            setDescription(getDescriptionHTML(task.inputs.description));
        } else {
            getDescriptionFromTranslationJson();
        }
    }, [props.task]);

    const getDescriptionHTML = (htmlContent : any) => (
        <div className="slds-box slds-theme_shade">
          <div dangerouslySetInnerHTML={{ __html: htmlContent }}></div>
        </div>
    );

    /**
     * Detects if the current task is a CQ Migration Task V2.
     */
    function isCQMigrationTaskV2(): boolean {
        return task.inputs.preMigrationTasks !== undefined || 
               task.inputs.postMigrationTasks !== undefined;
    }
    /**
     * Renders the subtask table for the current task.
     *
     * This function extracts subtask information from `task.inputs.subTasks`,
     * converts it into a list of objects containing `name` and `status`,
     * and then displays them in a table format using the CQSubTask component.
     *
     * @returns {JSX.Element} A table displaying subtasks when available, otherwise an empty fragment.
     */
    function renderSubtasks() {
        let subTasks = task.inputs.subTasks && Object.entries(task.inputs.subTasks).length > 0 ?
            Object.entries(task.inputs.subTasks).map((subSteps) => {
                return { name: subSteps[0], status: subSteps[1] };
            }) : [];
        return (
            subTasks && subTasks.length > 0 ?
                <>
                    <div className="slds-scrollable_x">
                    <table className="slds-table slds-table_cell-buffer slds-table_bordered slds-table_striped">
                        <thead>
                            <tr className="slds-line-height_reset">
                                <th className="" scope="col">
                                    <div className="slds-truncate" title="Opportunity Name">{t("task_subtask_name_column")}</div>
                                </th>
                                <th className="" scope="col">
                                    <div className="slds-truncate" title="Opportunity Name">{t("task_subtask_status_column")}</div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {subTasks.map((subtask) => subtask ? <CQSubTask key={subtask.name} subtask={subtask}></CQSubTask> : <></>)}
                        </tbody>
                    </table>
                    </div>
                </>
                : <></>
        )
    }
      
    const getDescriptionFromTranslationJson = () => {
        const descriptionKey = getDescriptionKey(taskName);
        setDescription( i18n.exists(descriptionKey)
          ? getDescriptionHTML(t(descriptionKey))
          : null)
    }
    let taskName = task.inputs.taskName;
    return <>
        <div key={taskName} className="slds-card" style={{border: 'solid 1px lightgray'}}>
            <div className="slds-card__header slds-grid">
                <header className="slds-media slds-media_center slds-has-flexi-truncate">
                    <div className="slds-media__figure">
                        { getTaskIcon(task) }
                    </div>
                    <div className="slds-media__body slds-text-align_left">
                        <h2 className="slds-card__header-title">
                            <span>{ (i18n.exists('task_' + taskName) ? t('task_' + taskName) : taskName)}</span>
                            {
                                task.isManual ?
                                <span className="slds-var-p-left_small slds-text-align_right" style={{zoom: 0.8}}><Badge  content={t('task_is_manual')} color="warning"
                                    icon={
                                        <Icon
                                            category="utility"
                                            name="warning"
                                            size="xx-small"
                                            colorVariant="base"
                                        />}
                                ></Badge></span>
                                : <></>
                            }
                        </h2>
                    </div>
                    <div className="slds-no-flex">
                        { !isReadonly ?  <>
                            <div>
                                {runIndividually ? getTaskButton(task) : 
                                <span>
                                    <Checkbox
                                        disabled={task.inputs.isOptional !== true}
                                        variant="toggle"
                                        labels={{
                                            toggleDisabled: t("deselected"),
                                            toggleEnabled: t("selected")
                                        }}
                                        checked={isSelected}
                                        onChange={(evt) => {
                                            if(props.onSelected) {
                                                props.onSelected(task, evt.target.checked);
                                            }
                                            setIsSelected(evt.target.checked);
                                        }}
                                    />
                                </span> }
                            </div>
                        </> : <></> }
                    </div>
                </header>
            </div>
            <div className="slds-card__body slds-card__body_inner slds-text-align_left">
                {/* TODO: replace dangerously set inner html with module like react-html-parser so that custom components can be rendered too, for now pure html components can only be rendered */}
                {description}
                {isCQMigrationTaskV2() ? (
                    <CQMigrationTaskV2 task={task} />
                ) : (
                    renderSubtasks()
                )}
            </div>
            <footer className="slds-card__footer">
                {
                    taskStatus === Statuses.Failed ?
                    
                        <div className="slds-form-element slds-has-error">
                            <div className="slds-form-element__help slds-text-align_left" id="error-01">{t("task_error_title")}</div>
                            <div className="slds-form-element__control">
                                <textarea className="slds-textarea" value={getErrorMessage(task)} rows={3} disabled></textarea>
                            </div>
                        </div>
                        : <></>
                }

            </footer>

        </div>
    </>

}